import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Users } from "lucide-react";
import api from "@/lib/api";
import PageBanner from "@/components/PageBanner";
import StatsCounter from "@/components/StatsCounter";
import { Button } from "@/components/ui/button";

const DEFAULT_STORY_IMAGE = "https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a";
const DEFAULT_STORY_BODY = "Gulfudaan was founded to make Gulf visa, GAMCA medical appointments, and travel documentation simple, fast, and transparent for thousands of Indian travelers and workers every year. From a small Lucknow office to a trusted name serving 25,000+ customers, our mission has stayed the same: genuine service, honest pricing, and real human support whenever you need it.";

export default function About() {
  const [about, setAbout] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/about-page").then((res) => setAbout(res.data)).catch(() => setAbout({}));
    api.get("/site-settings").then((res) => setSettings(res.data)).catch(() => {});
  }, []);

  if (!about) return null;

  return (
    <div data-testid="about-page" className="bg-white">
      <PageBanner pageKey="about" seoKey="about" eyebrow="About Us" title="About Gulfudaan" titleTestId="about-page-title" />

      <section className="py-8 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <img data-testid="about-story-image" src={about.story_image || DEFAULT_STORY_IMAGE} alt="Gulfudaan team" className="w-full h-64 sm:h-96 object-cover rounded-2xl" />
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--brand-primary)]">Our Story</span>
            <h2 data-testid="about-story-title" className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#1A1A1A] mt-3 mb-4">{about.story_title || "Trusted Since Day One"}</h2>
            <p data-testid="about-story-body" className="text-[var(--brand-secondary)] leading-relaxed">{about.story_body || DEFAULT_STORY_BODY}</p>
          </div>
        </div>
      </section>

      <StatsCounter />

      {about.team?.length > 0 && (
        <section className="py-16 sm:py-20 bg-[#FDFCFB]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--brand-primary)]">Our People</span>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#1A1A1A] mt-3">Meet The Team</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
              {about.team.map((m, i) => (
                <div key={i} data-testid={`about-team-member-${i}`} className="text-center p-5 rounded-lg border border-[#E5E1DA] bg-white">
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} className="w-20 h-20 rounded-full object-cover mx-auto mb-3" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-[color-mix(in_oklab,var(--brand-primary),transparent_88%)] flex items-center justify-center mx-auto mb-3">
                      <Users size={26} className="text-[var(--brand-primary)]" />
                    </div>
                  )}
                  <p className="font-heading text-base text-[#1A1A1A]">{m.name}</p>
                  <p className="text-xs text-[var(--brand-primary)] font-medium mt-0.5">{m.role}</p>
                  {m.bio && <p className="text-xs text-[var(--brand-secondary)] mt-2 leading-relaxed">{m.bio}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {about.office_photos?.length > 0 && (
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--brand-primary)]">Visit Us</span>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#1A1A1A] mt-3">Our Office</h2>
              {settings?.address && (
                <p data-testid="about-office-address" className="flex items-center justify-center gap-2 text-sm text-[var(--brand-secondary)] mt-3">
                  <MapPin size={15} className="text-[var(--brand-primary)]" /> {settings.address}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {about.office_photos.map((url, i) => (
                <img key={i} data-testid={`about-office-photo-${i}`} src={url} alt={`Office ${i + 1}`} className="w-full h-40 sm:h-52 object-cover rounded-lg" />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-[#1A1A1A] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="font-heading text-2xl sm:text-3xl text-white mb-4">Ready to Get Started?</h2>
          <p className="text-gray-400 mb-6 text-sm sm:text-base">Explore our services or reach out - our team responds fast, every day.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/#services" data-testid="about-cta-services-btn">
              <Button className="bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white">Explore Services</Button>
            </Link>
            <Link to="/contact" data-testid="about-cta-contact-btn">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">Contact Us</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
